// Tip Dialog Component
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Heart, DollarSign, Percent, CreditCard, ArrowLeftRight } from 'lucide-react';

const TIP_PERCENTAGES = [0, 10, 15, 20];

// Comisiones por tipo de tarjeta (con IVA 16% incluido)
const CARD_FEES = {
  TDD: { rate: 0.019 * 1.16, label: 'Débito', description: '1.9% + IVA' },
  TDC: { rate: 0.01 * 1.16, label: 'Crédito', description: '1% + IVA' },
  TRANSFER: { rate: 0, label: 'Transferencia', description: 'Sin comisión' }
};

export function TipDialog({ isOpen, onClose, subtotal, onConfirm, paymentMethod = 'card', branchClabe = '', branchBanco = '', branchBeneficiario = '' }) {
  const [tipAmount, setTipAmount] = useState(0);
  const [selectedPercentage, setSelectedPercentage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [cardType, setCardType] = useState(paymentMethod === 'transfer' ? 'TRANSFER' : 'TDD');

  // Format CLABE with spaces for readability
  const formatClabe = (clabe) => {
    if (!clabe) return '';
    return clabe.replace(/(\d{6})(\d{6})(\d{6})/g, '$1 $2 $3');
  };

  // Reset state when dialog opens
  const resetState = () => {
    setTipAmount(0);
    setSelectedPercentage(0);
    setCustomAmount('');
    setCardType(paymentMethod === 'transfer' ? 'TRANSFER' : 'TDD');
  };

  // Handle dialog open state change
  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
    }
  };

  const handlePercentageSelect = (percentage) => {
    setSelectedPercentage(percentage);
    const calculated = Math.round(subtotal * (percentage / 100) * 100) / 100;
    setTipAmount(calculated);
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    setSelectedPercentage(null);
    const amount = parseFloat(value) || 0;
    setTipAmount(amount);
  };

  const handleConfirm = () => {
    onConfirm(tipAmount, cardType);
    onClose();
  };

  const total = subtotal + tipAmount;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} onAnimationEnd={() => isOpen && resetState()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] p-0 overflow-hidden" onOpenAutoFocus={resetState}>
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Agregar Propina
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-80px)] px-4 pb-4">
          <div className="space-y-6 mt-4">
          {/* Subtotal Display */}
          <div className="flex justify-between items-center p-3 bg-zinc-800 rounded-lg">
            <span className="text-zinc-400">Subtotal del servicio:</span>
            <span className="text-xl font-semibold text-white">${subtotal.toFixed(2)}</span>
          </div>

          {/* Percentage Buttons */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Porcentaje de propina
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {TIP_PERCENTAGES.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handlePercentageSelect(percentage)}
                  className={`py-3 rounded-lg border transition-all ${
                    selectedPercentage === percentage
                      ? 'bg-pink-500/20 border-pink-500 text-pink-500'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <span className="text-lg font-semibold">{percentage}%</span>
                  <span className="block text-xs mt-1">
                    {percentage === 0 ? 'Sin' : `$${(subtotal * percentage / 100).toFixed(0)}`}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Monto personalizado
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={customAmount}
                onChange={(e) => handleCustomAmount(e.target.value)}
                placeholder="Ingresa monto"
                className="bg-zinc-800 border-zinc-700 text-white text-lg pl-9 h-12"
                data-testid="tip-custom-input"
              />
            </div>
          </div>

          {/* Card Type Selector - Only for card payments */}
          {paymentMethod === 'card' && (
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Tipo de Tarjeta
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCardType('TDD')}
                  className={`p-4 rounded-lg border transition-all ${
                    cardType === 'TDD'
                      ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="card-type-tdd"
                >
                  <p className="font-semibold">TDD</p>
                  <p className="text-xs mt-1">Débito</p>
                </button>
                <button
                  onClick={() => setCardType('TDC')}
                  className={`p-4 rounded-lg border transition-all ${
                    cardType === 'TDC'
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                  data-testid="card-type-tdc"
                >
                  <p className="font-semibold">TDC</p>
                  <p className="text-xs mt-1">Crédito</p>
                </button>
              </div>
            </div>
          )}

          {/* Transfer Info */}
          {paymentMethod === 'transfer' && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-green-400">
                <ArrowLeftRight className="w-5 h-5" />
                <span className="font-medium">Pago por Transferencia</span>
              </div>
              <p className="text-sm text-zinc-400">Sin comisión por propina</p>
              
              {/* Datos Bancarios */}
              {(branchClabe || branchBanco || branchBeneficiario) && (
                <div className="mt-3 p-3 bg-zinc-800 rounded-lg border border-zinc-700 space-y-3">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Datos para transferencia</p>
                  
                  {branchBanco && (
                    <div>
                      <p className="text-xs text-zinc-500">Banco</p>
                      <p className="text-base font-medium text-white">{branchBanco}</p>
                    </div>
                  )}
                  
                  {branchBeneficiario && (
                    <div>
                      <p className="text-xs text-zinc-500">Beneficiario</p>
                      <p className="text-base font-medium text-white">{branchBeneficiario}</p>
                    </div>
                  )}
                  
                  {branchClabe && (
                    <div>
                      <p className="text-xs text-zinc-500">CLABE Interbancaria</p>
                      <p className="text-lg font-mono text-white tracking-widest select-all">
                        {formatClabe(branchClabe)}
                      </p>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(branchClabe);
                        }}
                        className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        Copiar CLABE
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="space-y-2 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Subtotal:</span>
              <span className="text-white">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Propina:</span>
              <span className={tipAmount > 0 ? 'text-pink-500' : 'text-zinc-500'}>
                ${tipAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-zinc-700">
              <span className="text-white font-medium">Total a cobrar:</span>
              <span className="text-xl font-bold text-gold">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-gold text-black hover:bg-gold-dark"
              data-testid="tip-confirm-btn"
            >
              Continuar
            </Button>
          </div>
        </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
